import { useEffect, useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import {
    IPaymentOptionsForm,
    PaymentOptionsFieldError,
    initialPaymentOptionsForm,
    validatePaymentOptionsForm,
} from '../models/payment-options.model';
import { useItineraryPaymentOptions, useSaveItineraryPaymentOptions } from './usePaymentOptions';

/**
 * Lo step "Pagamento" dei flussi di invio (in piattaforma e link rapido): carica la
 * configurazione della proposta, tiene il form, valida e salva.
 *
 * Il salvataggio avviene prima di send-quote / create-shared-link, cosi' quelle edge
 * function non cambiano. Se l'invio poi fallisce resta salvata una configurazione su una
 * proposta non inviata: innocuo, al prossimo tentativo il form riparte da li'.
 */
export const useProposalPaymentStep = (itineraryId: string) => {
    const { t } = useI18n();
    const { options, isLoading } = useItineraryPaymentOptions(itineraryId);
    const saveMutation = useSaveItineraryPaymentOptions();

    const [form, setForm] = useState<IPaymentOptionsForm | null>(null);
    const [errors, setErrors] = useState<Set<PaymentOptionsFieldError>>(new Set());

    const stripeActive = !!options?.payee_stripe_active;

    // Solo la prima inizializzazione: un refetch non deve cancellare cio' che il TA sta scrivendo.
    useEffect(() => {
        if (options && form === null) setForm(initialPaymentOptionsForm(options, stripeActive));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    const onChange = (next: IPaymentOptionsForm) => {
        setForm(next);
        // Gli errori si ricalcolano solo dopo il primo tentativo, non a ogni tasto.
        if (errors.size > 0) setErrors(validatePaymentOptionsForm(next));
    };

    /** true se il form e' valido; altrimenti evidenzia i campi. */
    const validate = (): boolean => {
        if (!form) return false;
        const next = validatePaymentOptionsForm(form);
        setErrors(next);
        return next.size === 0;
    };

    const save = async () => {
        if (!form) throw new Error('payment_options_not_ready');
        return saveMutation.mutateAsync({
            itineraryId,
            // La nota vuota resta NULL: il cliente vedra' il testo standard nella sua lingua.
            form: form.bank_receipt_note.trim() === t('paymentOptions.bank.defaultReceiptNote').trim()
                ? { ...form, bank_receipt_note: '' }
                : form,
        });
    };

    return {
        form,
        onChange,
        errors,
        validate,
        save,
        isLoading: isLoading || form === null,
        isSaving: saveMutation.isPending,
        stripeActive,
        defaults: options?.defaults ?? null,
    };
};
